import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { gsap } from "gsap";
import axios from "axios";
import Header from "./Header";
import Footer from "./Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAGE_GRADIENT = "linear-gradient(180deg, #0C1A19 0%, #080C0C 20%, #080C0C 80%, #040808 100%)";

const T = {
  muted: "rgba(255,255,255,0.45)",
};

const parseRichText = (text) => {
  if (!text) return "";
  return text
    .replace(/^## (.+)$/gm, '<span style="color:inherit;font-size:16px;font-weight:700;display:block;margin:12px 0 2px">$1</span>')
    .replace(/^### (.+)$/gm, '<span style="color:inherit;font-size:14px;font-weight:600;display:block;margin:8px 0 2px">$1</span>')
    .replace(/\*\*(.*?)\*\*/g, '<strong style="color:inherit;font-weight:600;font-family:inherit">$1</strong>')
    .replace(/__(.*?)__/g, '<u style="color:inherit;text-decoration:underline">$1</u>')
    .replace(/\*(.*?)\*/g, '<em style="color:inherit;font-style:italic">$1</em>')
    .replace(/^- (.+)$/gm, '<li style="color:inherit;margin-bottom:4px">$1</li>')
    .replace(/((?:<li[^>]*>.*?<\/li>)\n?)+/g, (m) => '<ul style="margin:6px 0 8px 20px;padding:0;list-style:disc">' + m.replace(/\n/g, "") + "</ul>")
    .replace(/\n/g, "<br>");
};

export function FaqAccordionItem({ question, answer, isOpen, onToggle }) {
  return (
    <div
      style={{
        background: "linear-gradient(145deg, #141f1e 0%, #0c1717 100%)",
        border: "1px solid",
        borderColor: isOpen ? "rgba(91,148,144,0.55)" : "rgba(91,148,144,0.22)",
        borderRadius: 10,
        overflow: "hidden",
        width: "100%",
        boxSizing: "border-box",
        transition: "all 0.25s ease",
        boxShadow: isOpen
          ? "0 0 36px rgba(91,148,144,0.26), 0 8px 28px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)"
          : "0 0 22px rgba(91,148,144,0.13), 0 4px 18px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)",
      }}
    >
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "20px 24px",
          fontFamily: "Montserrat, sans-serif",
          fontSize: 15,
          fontWeight: 500,
          color: "#fff",
          letterSpacing: -0.3,
          textAlign: "left",
        }}
      >
        <span dangerouslySetInnerHTML={{ __html: parseRichText(question) }} />
        <ChevronDown
          size={18}
          color="#7BB5B1"
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.25s ease",
            flexShrink: 0,
            marginLeft: 16,
          }}
        />
      </button>
      <div style={{ display: "grid", gridTemplateRows: isOpen ? "1fr" : "0fr", transition: "grid-template-rows 0.3s ease" }}>
        <div style={{ overflow: "hidden" }}>
          <p
            style={{
              margin: 0,
              padding: "16px 24px 24px",
              fontFamily: "Montserrat, sans-serif",
              fontSize: 13,
              fontWeight: 300,
              lineHeight: 1.9,
              color: "#e8e8e8",
              borderTop: "1px solid rgba(255,255,255,0.04)",
            }}
            dangerouslySetInnerHTML={{ __html: parseRichText(answer) }}
          />
        </div>
      </div>
    </div>
  );
}

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [faqItems, setFaqItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const headingRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/faq`)
      .then((res) => {
        if (res.data.success) setFaqItems(res.data.data);
        else setError(true);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion || !headingRef.current) return;
    const ctx = gsap.context(() => {
      gsap.from(headingRef.current, { clipPath: "inset(0 100% 0 0)", duration: 0.9, ease: "power3.out" });
    });
    return () => ctx.revert();
  }, []);

  return (
    <div style={{ background: PAGE_GRADIENT, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Header />

      {/* Hero */}
      <section
        style={{
          paddingTop: 128,
          paddingBottom: 48,
          paddingLeft: "clamp(24px, 6vw, 80px)",
          paddingRight: "clamp(24px, 6vw, 80px)",
        }}
      >
        <h1
          ref={headingRef}
          style={{
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 300,
            fontSize: "clamp(36px, 5vw, 64px)",
            color: "#fff",
            letterSpacing: -2,
            lineHeight: 1.1,
            marginBottom: 20,
          }}
        >
          Frequently asked <span style={{ color: "#7BB5B1" }}>questions</span>
        </h1>
        <p
          style={{
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 300,
            fontSize: 15,
            color: T.muted,
            maxWidth: 520,
            lineHeight: 1.8,
          }}
        >
          Everything you need to know about working with Grassurn Tech.
        </p>
      </section>

      {/* FAQ list — flex:1 pushes footer to bottom */}
      <section
        style={{
          flex: 1,
          paddingLeft: "clamp(24px, 6vw, 80px)",
          paddingRight: "clamp(24px, 6vw, 80px)",
          paddingBottom: 120,
          paddingTop: 24,
          borderTop: "1px solid rgba(255,255,255,0.04)",
        }}
      >
        {loading ? (
          <p style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 14, color: T.muted }}>
            Loading FAQs…
          </p>
        ) : error ? (
          <p style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 14, color: T.muted }}>
            Unable to load FAQs right now. Please try again later.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%" }}>
            {faqItems.map((item, i) => (
              <FaqAccordionItem
                key={item.id}
                question={item.question}
                answer={item.answer}
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
              />
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;
